import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { translateMessageWithGoogle } from "../api/translateBatch";
import { normalizeLiveLanguageCode } from "../components/common/LiveTranslationDropdown";

/**
 * Debounced live translation for controlled inputs (e.g. contact message).
 * Typing in English + ASCII skips Google (avoids glitches). Language changes
 * (`retranslateCurrent`, immediate) always call Google so round-trips restore
 * the right script (e.g. French → English).
 */
export function useLiveTranslatedInput() {
  const { i18n } = useTranslation();
  const messageTargetLang = useMemo(
    () =>
      normalizeLiveLanguageCode(
        i18n.resolvedLanguage || i18n.language,
        "en"
      ),
    [i18n.resolvedLanguage, i18n.language]
  );

  const [isTranslating, setIsTranslating] = useState(false);
  const draftRef = useRef("");
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);
  const inflightRef = useRef(0);

  useEffect(
    () => () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    },
    []
  );

  const scheduleTranslation = useCallback(
    (text: string, apply: (s: string) => void, immediate: boolean) => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      const run = async (requestId: number, isLanguageChange: boolean) => {
        if (requestId !== requestIdRef.current) {
          return;
        }
        const liveText = draftRef.current || text;
        if (liveText.trim().length < 2) {
          return;
        }
        const latinAsciiOnly =
          liveText.length > 0 && !/[^\x00-\x7F]/.test(liveText);
        if (
          !isLanguageChange &&
          messageTargetLang === "en" &&
          latinAsciiOnly
        ) {
          return;
        }

        inflightRef.current += 1;
        setIsTranslating(true);
        try {
          const out = await translateMessageWithGoogle(
            liveText,
            messageTargetLang
          );
          if (requestId !== requestIdRef.current) {
            return;
          }
          const norm = (s: string) => s.trim().normalize("NFC");
          if (
            typeof out === "string" &&
            out.length > 0 &&
            norm(out) !== norm(liveText)
          ) {
            apply(out);
            draftRef.current = out;
          }
        } finally {
          inflightRef.current -= 1;
          if (inflightRef.current <= 0) {
            inflightRef.current = 0;
            setIsTranslating(false);
          }
        }
      };

      const startRun = () => {
        const requestId = ++requestIdRef.current;
        void run(requestId, immediate);
      };

      if (immediate) {
        startRun();
        return;
      }
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        startRun();
      }, 450);
    },
    [messageTargetLang]
  );

  const bindLiveText = useCallback(
    (next: string, apply: (s: string) => void) => {
      requestIdRef.current += 1;
      draftRef.current = next;
      apply(next);
      scheduleTranslation(next, apply, false);
    },
    [scheduleTranslation]
  );

  const retranslateCurrent = useCallback(
    (getValue: () => string, apply: (s: string) => void) => {
      requestIdRef.current += 1;
      const v = getValue();
      draftRef.current = v;
      if (v.trim().length >= 2) {
        scheduleTranslation(v, apply, true);
      }
    },
    [scheduleTranslation]
  );

  const clearDraft = useCallback(() => {
    draftRef.current = "";
  }, []);

  const cancelPendingTranslation = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    requestIdRef.current += 1;
  }, []);

  const syncLiveDraft = useCallback((s: string) => {
    draftRef.current = s;
  }, []);

  return {
    bindLiveText,
    retranslateCurrent,
    clearDraft,
    cancelPendingTranslation,
    syncLiveDraft,
    isTranslating,
    messageTargetLang,
  };
}
